import { inject, Injectable, signal } from '@angular/core';
import { Entity } from '@approot/models';
import { environment } from '@root/environments/environment';
import { Observable, take } from 'rxjs';
import { AuthService } from '../auth/auth.service';

type EntitiesMap = {
  entities?: string[];
  realEntities?: string[];
};

/**
 * Some banks are very sensitive about exposing client names externally, instead using an ID/code
 * when refered to in transactions.   However, that is impractical for traders who need to see the actual
 * names.  This service provides a mapping between the two.
 */
@Injectable({
  providedIn: 'root'
})
export class EntityMapperService {
  private authService = inject(AuthService);
  public entities = signal<EntitiesMap>({ entities: [], realEntities: [] });
  public allEntities = signal<Entity[]>([]);
  public hasDisplayNames = signal<boolean>(false);

  constructor() {
    this.getEntities().subscribe({
      next: (entities: Entity[]) => {
        this.allEntities.set(entities);
        // DON'T sort as we need both arrays to "match"
        const displayedEntities = entities.map(
          e => e.shortDisplayName || e.identifier
        );
        const realEntities = entities.map(e => e.identifier);
        this.entities.set({ entities: displayedEntities, realEntities });
      }
    });
  }

  public getRealEntity(entity: string): string {
    const entityIdx = this.entities().entities.findIndex(e => e === entity);
    return this.entities().realEntities[entityIdx] || entity;
  }

  public getDisplayEntity(entity: string): string {
    const entityIdx = this.entities().realEntities.findIndex(e => e === entity);
    return this.entities().entities[entityIdx] || entity;
  }

  public getExtendedDisplayTitle(entity: string): string {
    const realEntity = this.getRealEntity(entity);
    const e = this.allEntities().find(v => v.identifier === realEntity);
    if (e?.shortDisplayName) {
      return `${e.shortDisplayName}`;
    } else {
      return entity;
    }
  }

  /**
   * Any outbound payload potentially containg sensitive information should be "cleaned"
   * with this function.   We could probably clean ALL outbound payloads to be safe.
   * @param storage the object to clean
   * @returns the cleaned object
   */
  public cleanConfidentialFields(storage: any, task = ''): any {
    const updateNestedObject = (obj: any) => {
      if (obj instanceof Array) {
        for (var i in obj) {
          updateNestedObject(obj[i]);
        }
      } else {
        for (var key in obj) {
          if (this.isPrivateKey(key) && typeof obj[key] === 'string') {
            const replacement = this.getRealEntity(obj[key]);
            if (replacement != null && replacement !== obj[key]) {
              if (environment.featureFlags.logEntityMapper) {
                const context = task ? `task=${task}` : '';
                console.log(
                  'Replacing',
                  key,
                  '=>',
                  obj[key],
                  'with',
                  replacement,
                  context
                );
              }
              obj[key] = replacement;
            }
          }
          if (obj[key] instanceof Object || obj[key] instanceof Array) {
            updateNestedObject(obj[key]);
          }
        }
      }
    };

    updateNestedObject(storage);
    return storage;
  }

  public uncleanConfidentialFields(storage: any, task = ''): any {
    const updateNestedObject = (obj: any) => {
      if (obj instanceof Array) {
        for (var i in obj) {
          updateNestedObject(obj[i]);
        }
      } else {
        for (var key in obj) {
          if (this.isPrivateKey(key) && typeof obj[key] === 'string') {
            const replacement = this.getDisplayEntity(obj[key]);
            if (replacement != null && replacement !== obj[key]) {
              obj[key] = replacement;
            }
          }
          if (obj[key] instanceof Object || obj[key] instanceof Array) {
            updateNestedObject(obj[key]);
          }
        }
      }
    };

    updateNestedObject(storage);
    return storage;
  }

  private getEntities(): Observable<Entity[]> {
    // Duplicated from TradeCaptureService because of circular dependency
    // NOTE: ensure the URL contains "fx/get-user-info?task=getEntities"
    // As this is used to detect this call to redact the response from Raygun
    const task = 'getEntities';
    return this.authService
      .fxPost<Entity[]>({ task }, 'get-user-info', `task=${task}`)
      .pipe(take(1));
  }

  private isPrivateKey(key: string): boolean {
    return (
      key === 'entity' ||
      key === 'counterPartyA' ||
      key === 'counterPartyB' ||
      key === 'primeBroker' ||
      key === 'account' ||
      key === 'aggregateLegsEntity'
    );
  }
}

export function sorter(a: string, b: string) {
  return a.localeCompare(b, 'en', { sensitivity: 'accent' });
}
